import {
  useState,
} from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
  DateTime,
} from 'luxon';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
  type Subscription,
} from '@gymcloud/core';

import {
  Icon,
  Dropdown,
  AddCardForm,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';
import CouponForm, { type CouponState } from 'web-components/CouponForm/CouponForm';
import config from 'web-config';
import { getRoundedDollar } from 'web-utils';

import * as S from './styled';

interface Props {
  couponState: CouponState;
  goTo: (stap: string) => void;
  onCheckCoupon: (coupon: string) => void;
}

const stripePromise = loadStripe(config.stripe.key);

const getNextText = (subscription?: Subscription) => {
  let text = subscription?.cancel_at_period_end ? 'Subscribed Until:' : 'Next Payment:';
  if (subscription?.status === 'past_due') { text = 'Next Payment Attempt:'; }
  return text;
}

const getNextDate = (subscription: Subscription) => {
  const date = DateTime.fromMillis(subscription?.current_period_end * 1000).toFormat('MMMM dd, yyyy')


  return `$${getRoundedDollar(subscription.plan.amount)}, ${date}`;
}

const mapState = state => ({
  defaultCardId: coreSelectors.defaultCardIdSelector(state),
  paymentMethods: coreSelectors.getResourceMappedList(
    'paymentMethods',
  )(state),
  subscriptions: coreSelectors.getSubscriptions(state),
});

const Overview = ({
  couponState,
  goTo,
  onCheckCoupon
}: Props) => {
  const {
    subscriptions,
    defaultCardId,
    paymentMethods,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const [cardFormIsVisible, setCardFormVisible] = useState(false);
  const subscription = subscriptions[0] // TODO fix

  const onSetDefaultPaymentMethod = (method: any) => {
    dispatch(
      coreActions.requestSetDefaultPaymentMethod({
        id: method.id,
      }),
    )
  }

  const onDeletePaymentMethod = (method: any) => {
    dispatch(
      coreActions.requestDeletePaymentMethod({
        id: method.id,
      }),
    )
  }

  const handleAddCard = (res: any, actions: any) => {
    dispatch(
      coreActions.requestCreatePaymentMethod({
        body: {
          id: res.paymentMethod.id,
        },
        callbacks: {
          ...actions,
          onSuccess: () => {
            setCardFormVisible(false);
          },
        },
      }),
    );
  }

  const actions = [
    {
      name: 'Change',
      onClick: () => goTo('plans'),
    },
    {
      name: 'Cancel',
      color: '#e54d42',
      onClick: () => goTo('cancel'),
    },
  ]

  return (
    <Flex column spacing={30}>
      <S.Card>
        <Flex width="100%" justify="space-between" align="center">
          <Flex spacing={10} align="center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="19"
              height="15"
              fill="none"
              viewBox="0 0 19 15"
            >
              <path
                fill="#333"
                d="M1.55 10.507.45 3.343a.6.6 0 0 1 .948-.564l3.433 2.57a.82.82 0 0 0 1.175-.2L8.865.859a.763.763 0 0 1 1.27 0l2.859 4.289a.82.82 0 0 0 1.175.2l3.433-2.57a.6.6 0 0 1 .949.57l-1.1 7.164zM16.62 14.48H2.379a.83.83 0 0 1-.83-.828V11.83h15.9v1.82a.83.83 0 0 1-.83.83"
              />
            </svg>
            <S.Title>Subscriptions</S.Title>
          </Flex>

          <Dropdown
            trigger={(
              <S.ActionsTrigger />
            )}
            items={actions}
          />
        </Flex>
        <Text size="20px" color="#666666">
          {subscription?.plan?.name}
        </Text>

        {subscription?.id && (
          <Flex column>
            <Text color="#333333">{getNextText(subscription)}</Text>
            <Text size="16px" color="#666666">
              {getNextDate(subscription)}
            </Text>
          </Flex>
        )}

        <hr />
        <CouponForm buttonText="Apply" couponState={couponState} onSubmit={onCheckCoupon} />
      </S.Card>

      <S.Card>
        <Flex width="100%" justify="space-between" align="center">
          <Flex spacing={10} align="center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="19"
              height="14"
              fill="none"
              viewBox="0 0 19 14"
            >
              <path
                fill="#333"
                d="M18.318 12.047v-6.37H.683v6.37a1.856 1.856 0 0 0 1.856 1.856h13.923a1.856 1.856 0 0 0 1.856-1.856m-2.573-.76h-2.532a.591.591 0 0 1 0-1.181h2.531a.591.591 0 0 1 0 1.182zM18.318 2.554A1.856 1.856 0 0 0 16.462.698H2.54A1.857 1.857 0 0 0 .683 2.554v1.944h17.635z"
              />
            </svg>
            <S.Title>Payment Methods</S.Title>
          </Flex>
        </Flex>

        <Flex
          column
          width="100%"
          spacing={10}
        >
          {paymentMethods.map(m => (
            <Flex
              key={m.id}
              width="100%"
              justify="space-between"
            >
              <Flex
                width="100%"
                align="baseline"
                spacing={10}
                clickable
              >
                <Flex column>
                  <Text
                    size="18px"
                    color="#666666"
                  >
                    {`**** ${m.card.last4}`}
                  </Text>
                  <Text
                    size="14px"
                    color="#666666"
                  >
                    {`EXP: ${m.card.exp_month}/${m.card.exp_year}`}
                  </Text>
                </Flex>
                {m.id === defaultCardId && (
                  <span className="success-color">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width={18}
                      height={18}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                  </span>
                )}
              </Flex>

              <Dropdown
                trigger={(
                  <S.ActionsTrigger />
                )}
                items={[
                  ...(m.id === defaultCardId
                    ? []
                    : [
                      {
                        name: 'Set as Default',
                        onClick: () => onSetDefaultPaymentMethod(m),
                      },
                    ]),

                  {
                    name: 'Delete',
                    color: '#e54d42',
                    onClick: () => onDeletePaymentMethod(m),
                  },
                ]}
              />
            </Flex>
          ))}
        </Flex>

        {cardFormIsVisible ? (
          <Flex
            width="100%"
            padding="10px 0"
            column
          >
            <hr />
            <Elements stripe={stripePromise}>
              <AddCardForm
                submitText="Add Card"
                onCancel={() => setCardFormVisible(false)}
                onSuccess={handleAddCard}
              />
            </Elements>
            <hr />
          </Flex>
        ) : (
          <S.AddCardButton onClick={() => setCardFormVisible(true)}>
            Add New Card
          </S.AddCardButton>
        )}
      </S.Card>
    </Flex>
  );
};

export default Overview;
